import React from "react";
import { Flex, Tooltip } from "@chakra-ui/react";
import { SA_COLORS } from "./sensor-alert-colors";

/**
 * Two-state pill button used by the periodicity toggle (Recorrente / Único)
 * inside the rain and lightning configuration cards.
 *
 * Visual matches Figma node 3614:4463 — subtle primary fill + primary border
 * + primary text when active; transparent fill + neutral border + body text
 * when inactive. Equal flex distribution among siblings.
 *
 * Use the native `aria-pressed` to expose the toggle state to assistive tech.
 */
export interface TogglePillButtonProps {
  active: boolean;
  onClick: () => void;
  /** Visible label rendered inside the pill. */
  label: string;
  /** Optional rich tooltip body shown on hover (Chakra `Tooltip`). */
  tooltip?: React.ReactNode;
}

export const TogglePillButton: React.FC<TogglePillButtonProps> = ({
  active,
  onClick,
  label,
  tooltip,
}) => {
  const pill = (
    <Flex
      as="button"
      type="button"
      aria-pressed={active}
      flex="1"
      justify="center"
      align="center"
      padding="10px"
      borderRadius="8px"
      cursor="pointer"
      fontWeight="500"
      fontSize="14px"
      lineHeight="1.21"
      transition="all 0.15s"
      onClick={onClick}
      bg={active ? SA_COLORS.primarySubtleBg : "transparent"}
      border="1px solid"
      borderColor={active ? SA_COLORS.primary : SA_COLORS.panelBorder}
      color={active ? SA_COLORS.primary : SA_COLORS.textBody}
    >
      {label}
    </Flex>
  );

  if (!tooltip) return pill;

  return (
    <Tooltip
      label={tooltip}
      placement="top"
      bg="white"
      fontSize="12px"
      lineHeight="1.33"
      borderRadius="8px"
      boxShadow="0px 2px 8px rgba(0, 0, 0, 0.25)"
      p="10px"
      maxW="238px"
      openDelay={300}
    >
      {pill}
    </Tooltip>
  );
};
